import { PLAN_NODE_LIFECYCLE_STATUSES, PLAN_STATE_STATUSES, PlanStateSchema } from '@aline/common';
import type { NodeOutput, PlanNodeLifecycleState, PlanState } from '@aline/common';

export function mergePlanStateSnapshot(existing: unknown, incoming: unknown): unknown {
  const incomingResult = PlanStateSchema.safeParse(incoming);
  if (!incomingResult.success) return incoming;

  const existingResult = PlanStateSchema.safeParse(existing);
  if (!existingResult.success) return incomingResult.data;

  return mergeIncomingPlanSnapshot(existingResult.data, incomingResult.data);
}

export function mergeIncomingPlanSnapshot(
  current: PlanState | null | undefined,
  incoming: PlanState
): PlanState {
  if (!current || incoming.status !== PLAN_STATE_STATUSES.executing) return incoming;

  return {
    ...incoming,
    nodeOutputs: mergeNodeOutputs({
      currentOutputs: current.nodeOutputs,
      currentStates: current.nodeStates,
      incomingOutputs: incoming.nodeOutputs,
      incomingStates: incoming.nodeStates,
    }),
    nodeStates: mergeNodeStates(current.nodeStates, incoming.nodeStates),
  };
}

function mergeNodeOutputs({
  currentOutputs,
  currentStates,
  incomingOutputs,
  incomingStates,
}: {
  currentOutputs: PlanState['nodeOutputs'];
  currentStates: PlanState['nodeStates'];
  incomingOutputs: PlanState['nodeOutputs'];
  incomingStates: PlanState['nodeStates'];
}) {
  const mergedOutputs = { ...currentOutputs };

  for (const [nodeId, incomingOutput] of Object.entries(incomingOutputs)) {
    mergedOutputs[nodeId] = mergeNodeOutput({
      currentOutput: currentOutputs[nodeId],
      currentState: currentStates[nodeId],
      incomingOutput,
      incomingState: incomingStates[nodeId],
    });
  }

  return mergedOutputs;
}

function mergeNodeOutput({
  currentOutput,
  currentState,
  incomingOutput,
  incomingState,
}: {
  currentOutput: NodeOutput | undefined;
  currentState: PlanNodeLifecycleState | undefined;
  incomingOutput: NodeOutput;
  incomingState: PlanNodeLifecycleState | undefined;
}): NodeOutput {
  if (!currentOutput) return incomingOutput;
  if (incomingOutput.status !== 'streaming') return incomingOutput;
  if (getIsResumedNodeSnapshot({ currentState, incomingState })) return incomingOutput;
  // Resume first reopens the state, then a later snapshot replaces the paused output.
  if (
    currentOutput.status === 'completed' &&
    currentState?.status === PLAN_NODE_LIFECYCLE_STATUSES.started &&
    incomingState?.status === PLAN_NODE_LIFECYCLE_STATUSES.started
  ) {
    return incomingOutput;
  }
  if (currentOutput.status !== 'streaming') return currentOutput;
  return currentOutput.displayText.length > incomingOutput.displayText.length
    ? currentOutput
    : incomingOutput;
}

function mergeNodeStates(
  currentStates: PlanState['nodeStates'],
  incomingStates: PlanState['nodeStates']
) {
  const mergedStates = { ...currentStates };

  for (const [nodeId, incomingState] of Object.entries(incomingStates)) {
    mergedStates[nodeId] = mergeNodeState(currentStates[nodeId], incomingState);
  }

  return mergedStates;
}

function mergeNodeState(
  currentState: PlanNodeLifecycleState | undefined,
  incomingState: PlanNodeLifecycleState
): PlanNodeLifecycleState {
  if (!currentState) return incomingState;
  if (incomingState.status !== 'started') return incomingState;
  if (getIsResumedNodeSnapshot({ currentState, incomingState })) return incomingState;
  return currentState.status === 'started' ? incomingState : currentState;
}

function getIsResumedNodeSnapshot({
  currentState,
  incomingState,
}: {
  currentState: PlanNodeLifecycleState | undefined;
  incomingState: PlanNodeLifecycleState | undefined;
}) {
  return (
    currentState?.status === PLAN_NODE_LIFECYCLE_STATUSES.awaitingInput &&
    incomingState?.status === PLAN_NODE_LIFECYCLE_STATUSES.started
  );
}
